import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Table, Button, Space, Tag, message, Card, Typography, Tooltip,
  Badge, Modal, Descriptions, Divider, Statistic, Row, Col
} from 'antd';
import {
  CheckCircleOutlined, CloseCircleOutlined, ClockCircleOutlined,
  EyeOutlined, ReloadOutlined, FileTextOutlined, PrinterOutlined, FileExcelOutlined
} from '@ant-design/icons';
import { getSampleConfirmations, confirmSample, rejectSample } from '../../services/limsService';
import { useReactToPrint } from 'react-to-print';

const { Title, Text } = Typography;

const SampleConfirmationsList = () => {
  const [confirmations, setConfirmations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedConfirmation, setSelectedConfirmation] = useState(null);
  const printRef = useRef();

  const loadConfirmations = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getSampleConfirmations();
      setConfirmations(data);
    } catch (error) {
      message.error('Failed to load sample confirmations');
      console.error('Error loading confirmations:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConfirmations();
  }, [loadConfirmations]);

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: 'LIMS Sample Confirmations Report',
    pageStyle: `
      @media print {
        @page {
          margin: 1cm;
          size: A4;
        }
        body { 
          -webkit-print-color-adjust: exact;
          font-family: Arial, sans-serif;
          font-size: 12px;
          line-height: 1.4;
        }
        /* Hide regular display layout */
        .ant-card { display: none !important; }
        .ant-modal { display: none !important; }
        
        /* Show print layout */
        .print-layout { display: block !important; }
        
        /* Print table styling */
        table { 
          width: 100% !important;
          border-collapse: collapse !important;
          margin-top: 20px;
        }
        th, td {
          border: 1px solid #000 !important;
          padding: 6px 8px !important;
          text-align: left;
          vertical-align: top;
        }
        th {
          background: #f5f5f5 !important;
          font-weight: bold;
        }
      }
    `,
    onBeforePrint: () => {
      console.log('Printing confirmations:', confirmations);
      console.log('Confirmations count:', confirmations.length);
      if (!confirmations || confirmations.length === 0) {
        alert('No confirmations to print. Please check if confirmations are being generated or try adjusting your filters.');
        return false;
      }
    }
  });

  const handleExportExcel = () => {
    if (!confirmations || confirmations.length === 0) return;
    
    const data = [
      ['LIMS Sample Confirmations Report'],
      ['Generated on:', new Date().toLocaleString()],
      [''],
      ['ID', 'Barcode', 'Sample Name', 'Patient', 'Status', 'Confirmed At', 'LIMS User', 'Error Message']
    ];

    confirmations.forEach(confirmation => {
      data.push([
        confirmation.id || '',
        confirmation.barcode || confirmation.delivery_item_barcode || '',
        confirmation.delivery_item_name || '',
        confirmation.patient_names || '',
        confirmation.status || '',
        confirmation.confirmed_at ? new Date(confirmation.confirmed_at).toLocaleString() : '',
        confirmation.lims_user_username || '',
        confirmation.error_message || ''
      ]);
    });

    // Convert to CSV and download
    const csvContent = data.map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `lims_sample_confirmations_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleConfirm = async (id) => {
    try {
      await confirmSample(id);
      message.success('Sample confirmed successfully');
      loadConfirmations();
    } catch (error) {
      message.error('Failed to confirm sample');
    }
  };

  const handleReject = async (id) => {
    try {
      await rejectSample(id, { error_message: 'Rejected by admin' });
      message.success('Sample rejected successfully');
      loadConfirmations();
    } catch (error) {
      message.error('Failed to reject sample');
    }
  };

  const handleViewDetails = (record) => {
    setSelectedConfirmation(record);
    setDetailModalVisible(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'CONFIRMED': return 'success';
      case 'REJECTED': return 'error';
      case 'PENDING': return 'warning';
      case 'ERROR': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'CONFIRMED': return <CheckCircleOutlined />;
      case 'REJECTED': return <CloseCircleOutlined />;
      case 'PENDING': return <ClockCircleOutlined />;
      case 'ERROR': return <CloseCircleOutlined />;
      default: return <ClockCircleOutlined />;
    }
  };

  const columns = [
    {
      title: 'Barcode',
      dataIndex: 'barcode',
      key: 'barcode',
      render: (text) => <Text code>{text}</Text>,
    },
    {
      title: 'Sample Name',
      dataIndex: 'delivery_item_name',
      key: 'delivery_item_name',
      render: (text) => text || '-',
    },
    {
      title: 'Patient',
      dataIndex: 'patient_names',
      key: 'patient_names',
      render: (text) => text || 'No patient data',
    },
    {
      title: 'Delivery Number',
      dataIndex: 'delivery_number',
      key: 'delivery_number',
      render: (text) => text || '-',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={getStatusColor(status)} icon={getStatusIcon(status)}>
          {status}
        </Tag>
      ),
    },
    {
      title: 'Confirmed By',
      dataIndex: 'lims_user_username',
      key: 'lims_user_username',
      render: (text) => text || 'System',
    },
    {
      title: 'Confirmed At',
      dataIndex: 'confirmed_at',
      key: 'confirmed_at',
      render: (date) => date ? new Date(date).toLocaleString() : '-',
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => new Date(date).toLocaleString(),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetails(record)}
            title="View Details"
          />
          {record.status === 'PENDING' && (
            <>
              <Button
                type="text"
                icon={<CheckCircleOutlined />}
                onClick={() => handleConfirm(record.id)}
                title="Confirm Sample"
              />
              <Button
                type="text"
                danger
                icon={<CloseCircleOutlined />}
                onClick={() => handleReject(record.id)}
                title="Reject Sample"
              />
            </>
          )}
        </Space>
      ),
    },
  ];

  // Calculate statistics
  const stats = {
    total: confirmations.length,
    confirmed: confirmations.filter(c => c.status === 'CONFIRMED').length,
    rejected: confirmations.filter(c => c.status === 'REJECTED').length,
    pending: confirmations.filter(c => c.status === 'PENDING').length,
  };

  return (
    <>
      {/* Regular Display Layout */}
      <Card>
        <div style={{ marginBottom: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Title level={4}>Sample Confirmations</Title>
            <Space className="no-print">
              <Button 
                icon={<PrinterOutlined />} 
                onClick={handlePrint}
              >
                Print
              </Button>
              <Button 
                icon={<FileExcelOutlined />} 
                onClick={handleExportExcel}
                disabled={!confirmations || confirmations.length === 0}
              >
                Export Excel
              </Button>
            </Space>
          </div>
          
          <Row gutter={16} style={{ marginTop: '16px' }}>
            <Col span={6}>
              <Statistic title="Total" value={stats.total} />
            </Col>
            <Col span={6}>
              <Statistic 
                title="Confirmed" 
                value={stats.confirmed} 
                valueStyle={{ color: '#52c41a' }}
              />
            </Col>
            <Col span={6}>
              <Statistic 
                title="Rejected" 
                value={stats.rejected} 
                valueStyle={{ color: '#ff4d4f' }}
              />
            </Col>
            <Col span={6}>
              <Statistic 
                title="Pending" 
                value={stats.pending} 
                valueStyle={{ color: '#faad14' }}
              />
            </Col>
          </Row>
        </div>

        <Table
          columns={columns}
          dataSource={confirmations}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} confirmations`,
          }}
        />
      </Card>

      {/* Print Layout - Hidden by default, shown only when printing */}
      <div ref={printRef} className="print-layout" style={{ display: 'none' }}>
        {/* Print Header */}
        <div style={{ textAlign: 'center', marginBottom: '20px', paddingBottom: '10px', borderBottom: '2px solid #000' }}>
          <h2 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '10px' }}>
            LIMS Sample Confirmations Report
          </h2>
          <div style={{ fontSize: '12px', color: '#666', marginBottom: '10px' }}>
            Generated on: {new Date().toLocaleString()}
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            Total: {stats.total} | Confirmed: {stats.confirmed} | Rejected: {stats.rejected} | Pending: {stats.pending}
          </div>
        </div>

        {/* Print Table */}
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
          <thead>
            <tr style={{ background: '#f5f5f5' }}>
              <th style={{ border: '1px solid #000', padding: '8px', textAlign: 'left' }}>ID</th>
              <th style={{ border: '1px solid #000', padding: '8px', textAlign: 'left' }}>Sample ID</th>
              <th style={{ border: '1px solid #000', padding: '8px', textAlign: 'left' }}>Patient Name</th>
              <th style={{ border: '1px solid #000', padding: '8px', textAlign: 'left' }}>Status</th>
              <th style={{ border: '1px solid #000', padding: '8px', textAlign: 'left' }}>Confirmed At</th>
              <th style={{ border: '1px solid #000', padding: '8px', textAlign: 'left' }}>Confirmed By</th>
              <th style={{ border: '1px solid #000', padding: '8px', textAlign: 'left' }}>Notes</th>
            </tr>
          </thead>
          <tbody>
            {confirmations.map(confirmation => (
              <tr key={confirmation.id}>
                <td style={{ border: '1px solid #000', padding: '6px' }}>{confirmation.id}</td>
                <td style={{ border: '1px solid #000', padding: '6px' }}>{confirmation.lims_sample_id || '-'}</td>
                <td style={{ border: '1px solid #000', padding: '6px' }}>
                  {confirmation.patient ? `${confirmation.patient.names}` : '-'}
                </td>
                <td style={{ border: '1px solid #000', padding: '6px' }}>{confirmation.status}</td>
                <td style={{ border: '1px solid #000', padding: '6px' }}>
                  {confirmation.confirmed_at ? new Date(confirmation.confirmed_at).toLocaleString() : '-'}
                </td>
                <td style={{ border: '1px solid #000', padding: '6px' }}>
                  {confirmation.confirmed_by ? confirmation.confirmed_by.username : '-'}
                </td>
                <td style={{ border: '1px solid #000', padding: '6px' }}>{confirmation.notes || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal
        title="Sample Confirmation Details"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            Close
          </Button>
        ]}
        width={800}
      >
        {selectedConfirmation && (
          <div>
            <Descriptions column={2} bordered>
              <Descriptions.Item label="Barcode" span={2}>
                <Text code>{selectedConfirmation.barcode}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Sample Name">
                {selectedConfirmation.delivery_item_name || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={getStatusColor(selectedConfirmation.status)}>
                  {selectedConfirmation.status}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Patient">
                {selectedConfirmation.patient_names || 'No patient data'}
              </Descriptions.Item>
              <Descriptions.Item label="Delivery Number">
                {selectedConfirmation.delivery_number || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="LIMS Sample ID">
                {selectedConfirmation.lims_sample_id || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Confirmed By">
                {selectedConfirmation.lims_user_username || 'System'}
              </Descriptions.Item>
              <Descriptions.Item label="Confirmed At">
                {selectedConfirmation.confirmed_at ? 
                  new Date(selectedConfirmation.confirmed_at).toLocaleString() : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Created At" span={2}>
                {new Date(selectedConfirmation.created_at).toLocaleString()}
              </Descriptions.Item>
            </Descriptions>

            {selectedConfirmation.error_message && (
              <>
                <Divider />
                <Title level={5}>Error Message</Title>
                <Text type="danger">{selectedConfirmation.error_message}</Text>
              </>
            )}

            {selectedConfirmation.confirmation_data && (
              <>
                <Divider />
                <Title level={5}>Confirmation Data</Title>
                <pre style={{ 
                  background: '#f5f5f5', 
                  padding: '12px', 
                  borderRadius: '4px',
                  overflow: 'auto',
                  maxHeight: '200px'
                }}>
                  {JSON.stringify(selectedConfirmation.confirmation_data, null, 2)}
                </pre>
              </>
            )}
          </div>
        )}
      </Modal>
    </>
  );
};

export default SampleConfirmationsList;
